import { useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

interface Testimonial {
  name: string;
  role: string;
  company: string;
  content: string;
  rating: number;
}

const TestimonialsSection = () => {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials: Testimonial[] = [
    {
      name: t("testimonials.sarahName"),
      role: t("testimonials.sarahRole"),
      company: t("testimonials.sarahCompany"),
      content: t("testimonials.sarah"),
      rating: 5,
    },
    {
      name: t("testimonials.michaelName"),
      role: t("testimonials.michaelRole"),
      company: t("testimonials.michaelCompany"),
      content: t("testimonials.michael"),
      rating: 5,
    },
    {
      name: t("testimonials.emmaName"),
      role: t("testimonials.emmaRole"),
      company: t("testimonials.emmaCompany"),
      content: t("testimonials.emma"),
      rating: 5,
    },
  ];

  const next = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prev = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const current = testimonials[currentIndex];

  return (
    <section className="pt-16 md:pt-24 pb-16 md:pb-24 px-4 md:px-6 relative overflow-hidden">
      <div className="container mx-auto max-w-4xl relative z-10 pt-12 md:pt-24">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-12 md:mb-16 text-center text-glow-hover uppercase">
          {t("testimonials.title")} <span className="text-holographic">{t("testimonials.stories")}</span>
        </h2>

        <div className="glass-card rounded-2xl p-6 md:p-8 lg:p-12 relative hover-scale">
          <div className="flex justify-center mb-4">
            {Array.from({ length: current.rating }).map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-[hsl(var(--holographic-cyan))] text-[hsl(var(--holographic-cyan))] hover-scale-sm" />
            ))}
          </div>

          <p className="text-lg md:text-xl lg:text-2xl text-center mb-6 md:mb-8 leading-relaxed hover-glow-text">
            "{current.content}"
          </p>

          <div className="text-center">
            <p className="font-bold text-lg hover-glow-text">{current.name}</p>
            <p className="text-muted-foreground">
              {current.role} at {current.company}
            </p>
          </div>

          <div className="flex justify-center items-center gap-4 mt-8">
            <Button
              variant="ghost"
              size="icon"
              onClick={prev}
              className="hover:text-[hsl(var(--holographic-cyan))]"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>

            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex
                      ? "bg-[hsl(var(--holographic-cyan))] w-8"
                      : "bg-muted"
                  }`}
                />
              ))}
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={next}
              className="hover:text-[hsl(var(--holographic-cyan))]"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
